import { Loader2, Globe, ExternalLink } from 'lucide-react';
import { AdminPage, PageHeader, Panel, adminLink, useAdminPageTitle } from '@/components/admin/ui';
import { SeoCheckCard } from '@/components/admin/SeoCheckCard';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useSeoAdminData } from '@/hooks/useLocalSeo';
import { useCustomDomainEntitlement } from '@/hooks/useDomainAdmin';
import { buildSeoChecklist } from '@/lib/seoChecklist';
import { liveCustomDomain, resolvePageSeo, tenantOrigin } from '@/lib/localSeo';

/** Website → Google & SEO: the SEO check plus a preview of the homepage's Google listing. */
const SeoAdmin = () => {
  useAdminPageTitle('Google & SEO');
  const { orgId, org } = useAdminOrg();
  const { data, isLoading, isError } = useSeoAdminData(orgId);
  const entitlement = useCustomDomainEntitlement(orgId);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" aria-busy="true">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <AdminPage width="narrow" className="max-w-3xl">
        <Panel title="Couldn’t load your SEO check">
          <p className="text-sm text-muted-foreground">Please refresh the page to try again.</p>
        </Panel>
      </AdminPage>
    );
  }

  const { items, score } = buildSeoChecklist({
    ...data,
    customDomainAllowed: entitlement.data ?? null,
  });

  const businessName = data.company?.company_name?.trim() || org?.name || '';
  const origin = tenantOrigin(org?.slug ?? '', liveCustomDomain(data.domains));
  const home = resolvePageSeo(
    { kind: 'home' },
    {
      businessName,
      company: data.company,
      services: data.services,
      row: data.seoRows.find((row) => row.page_slug === 'home') ?? null,
    },
  );

  return (
    <AdminPage width="narrow" className="max-w-3xl">
      <PageHeader description="Help local customers find you on Google. We handle the technical bits — your sitemap, business details for Google and page titles — automatically." />

      <SeoCheckCard items={items} score={score} />

      <Panel
        title="How your homepage looks on Google"
        icon={<Globe className="h-4 w-4 text-muted-foreground" />}
        description={home.usingDefaults.title || home.usingDefaults.description
          ? 'Written automatically from your services and areas. Edit it any time in the Page Builder.'
          : 'Using the title and description you wrote.'}
      >
        <div className="rounded-lg border border-border bg-background p-4" data-testid="seo-google-preview">
          <p className="truncate text-xs text-muted-foreground">{origin.replace(/^https:\/\//, '')}</p>
          <p className="mt-1 text-[17px] leading-snug text-blue-700 dark:text-blue-400">{home.title}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{home.description}</p>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Using Google Search Console? Submit your sitemap:{' '}
          <a href={`${origin}/sitemap.xml`} target="_blank" rel="noreferrer" className={adminLink}>
            {origin.replace(/^https:\/\//, '')}/sitemap.xml
            <ExternalLink className="ml-1 inline h-3 w-3" aria-hidden />
          </a>
        </p>
      </Panel>
    </AdminPage>
  );
};

export default SeoAdmin;
